import { EventEmitter, inject, Injectable } from '@angular/core'
import { Application, ApplicationType, ENV, LoggerService, Service, ServiceType } from '@danangelus/ngx-app-core'
import { ThreadUtils } from '@danangelus/ngx-utils'
import { AuthenticationService } from '../security/authentication.service'
import { SessionService } from '../security/session.service'
import { AppManagerService } from './app-manager.service'
import { SessionError } from '../../models/gateway.model'

const log: LoggerService = LoggerService.get('LoaderService')

/**
 * Service to handle basic application functions and service calls
 *
 * @author Dan Bennett
 */
@Injectable({
    providedIn: 'any'
})
export class LoaderService {

    startedInitialising = false
    configLoaded = false
    loadingAttempts = 0

    appLoaded = new EventEmitter<boolean>()

    private appManager = inject(AppManagerService)
    private sessionService = inject(SessionService)

    constructor() {
        this.loadingAttempts = 0
    }

    initialiseApp(): Promise<boolean> {

        return new Promise<boolean>((resolve: any) => {
            this.startedInitialising = true

            // Reset flags to be re-updated after service calls
            this.configLoaded = false

            this.loadConfiguration().then((loaded: boolean) => {
                this.configLoaded = loaded
                resolve(this.isLoaded())
                this.appLoaded.emit(true)

                log.debug('initialiseApp', `Loaded config:\n\n${JSON.stringify(ENV)}\n\n`)

                this.sessionService.isSessionActive().then((loggedIn: boolean | SessionError) => {

                    if (loggedIn instanceof SessionError) {
                        log.error(`Error checking is session is active! ${loggedIn.error}`)
                        return
                    }
                    log.debug('initialiseApp', 'Logged In? ' + loggedIn)

                    if (loggedIn) {
                        log.trace('initialiseApp', 'Loading session')
                        this.sessionService.loadSession().then((user: any) => {
                            AuthenticationService.authenticated.next(user)
                        })
                    }
                })
            })
        })
    }

    async loadConfiguration(): Promise<boolean> {
        let localConfigCache: any = localStorage.getItem('cache.env_config')
        if (localConfigCache && typeof localConfigCache === 'string') {
            localConfigCache = JSON.parse(localConfigCache)
        }
        if (localConfigCache?.configuration && new Date(localConfigCache.expires) > new Date()) {
            this.buildEnv(localConfigCache.configuration)
            setTimeout(() => {
                this.loadConfigurationFromBackend().then(() => log.debug('loadConfiguration', 'Loaded configuration asynchronously!'))
            }, 100)
            return true
        }

        return await this.loadConfigurationFromBackend()
    }

    async loadConfigurationFromBackend(): Promise<boolean> {
        log.trace('loadConfiguration', `Loading service and app configuration...`)
        try {
            const response: any = await this.appManager.loadEnvironment()
            if (response && response.services && response.applications) {

                const expires: Date = new Date()
                expires.setDate(expires.getDate() + 7)

                localStorage.setItem('cache.env_config', JSON.stringify({ configuration: response, expires: expires.getTime() }))
                this.buildEnv(response)
                return true
            }
        } catch (e) {
            log.debug('loadConfiguration', `Failed to load the configuration! ${e}`)
        }
        // Wait a bit longer each time
        await ThreadUtils.sleep(++this.loadingAttempts * 300)
        return await this.loadConfiguration()
    }

    private buildEnv(response: any): void {
        ENV.services = new Map<string, Service>()
        Object.keys(response.services).forEach((key: string) => {
            const svc: any = response.services[key]
            const service: Service = {
                baseUrl: svc.baseUrl,
                tokenHeader: svc.tokenHeader,
                api: new Map<string, string>()
            }
            if (svc.api) {
                Object.keys(svc.api).forEach((apiKey: string) => service.api.set(apiKey, svc.api[apiKey]))
            }
            ENV.services.set(ServiceType[key], service)
        })

        ENV.applications = new Map<string, Application>()
        Object.keys(response.applications).forEach((key: string) => {
            const app: any = response.applications[key]
            const application: Application = {
                baseUrl: app.baseUrl,
                loginRequired: app.loginRequired,
                api: new Map<string, string>()
            }
            if (app.api) {
                Object.keys(app.api).forEach((apiKey: string) => application.api.set(apiKey, app.api[apiKey]))
            }
            ENV.applications.set(ApplicationType[key], application)
        })

        ENV.config = new Map<string, string>()
        Object.keys(response.config).forEach((key: string) => ENV.config.set(key, response.config[key]))
    }

    isLoaded(): boolean {
        return this.configLoaded
    }
}
